import _ from 'lodash';
import * as Yup from 'yup';

import { channelOutletMapping, CONSTANT } from '../utils/constant';
import EN from '../../EN.json';
import FR from '../../FR.json';

const locale = localStorage.getItem('locale');
const translations = locale === 'FR' ? FR : EN;
const {
  USERS: {
    FORM: {
      VALIDATION_MESSAGE: { REQUIRED, EMAIL, NUMERIC, ALPHANUMERIC, MIN, MAX }
    }
  }
} = translations;

const validationJSON = {
  roleId: Yup.number().required(REQUIRED),
  firstName: Yup.string().required(REQUIRED),
  lastName: Yup.string().required(REQUIRED),
  email: Yup.string()
    .email(EMAIL)
    .required(REQUIRED),
  province: Yup.lazy(value =>
    typeof value === 'object'
      ? Yup.array()
          .nullable()
          .min(1)
          .required(REQUIRED)
      : Yup.number().required(REQUIRED)
  ),
  contactNumber: Yup.string()
    .required(REQUIRED)
    .matches(/^[0-9]+$/, NUMERIC),
  channel: Yup.array()
    .nullable()
    .min(1)
    .required(REQUIRED),
  partnerId: Yup.number()
    .nullable()
    .required(REQUIRED),
  outlet: Yup.string()
    .required(REQUIRED)
    .matches(/^[a-zA-Z0-9]+$/, ALPHANUMERIC)
    .min(10, MIN['10'])
    .max(10, MAX['10']),
  outletCode: Yup.string().required(REQUIRED),
  salesManager: Yup.string().when('channelId', {
    is: CONSTANT.CHANNELS.DEALER,
    then: Yup.string(),
    otherwise: Yup.string().required(REQUIRED)
  }),
  salesManagerEmail: Yup.string().when('channelId', {
    is: CONSTANT.CHANNELS.DEALER,
    then: Yup.string().email(EMAIL),
    otherwise: Yup.string()
      .required(REQUIRED)
      .email(EMAIL)
  }),
  channelOrgCode: Yup.string()
    .required(REQUIRED)
    .matches(/^[0-9]+$/, NUMERIC)
    .min(5, MIN['5'])
    .max(5, MAX['5']),
  adtRepId: Yup.string()
    .required(REQUIRED)
    .matches(/^[0-9]+$/, NUMERIC),
  xid: Yup.string()
    .required(REQUIRED)
    .matches(/^[a-zA-Z0-9]+$/, ALPHANUMERIC)
    .min(7, MIN['7'])
    .max(7, MAX['7']),
  salesRepId: Yup.string()
    .required(REQUIRED)
    .matches(/^[a-zA-Z0-9]+$/, ALPHANUMERIC)
    .min(4, MIN['4'])
    .max(4, MAX['4']),
  dealership: Yup.array()
    .nullable()
    .min(1)
    .required(REQUIRED)
};

const defaultBaseMapping = [
  { key: 'roleId', mapping: 'roleId' },
  { key: 'firstName', mapping: 'firstName' },
  { key: 'lastName', mapping: 'lastName' },
  { key: 'email', mapping: 'email' },
  { key: 'province', mapping: 'province' },
  { key: 'channel', mapping: 'channel' },
  { key: 'contactNumber', mapping: 'contactNumber' },
  { key: 'salesManager', mapping: 'salesManager' },
  { key: 'salesManagerEmail', mapping: 'salesManagerEmail' }
];

const commonBetweenRepAndManager = [
  { key: 'outlet', mapping: 'outlet' },
  { key: 'salesRepId', mapping: 'salesRepId' }
];

const xidField = [{ key: 'xid', mapping: 'xid' }];

const rolebaseMapping = {
  ADMIN: xidField,
  SALES_SUPPORT: xidField,
  ORDER_FULFILLMENT: xidField
};

const channelCorpStoreOnly = [{ key: 'outletCode', mapping: 'outletCode' }];

const channelTqPartenaireOnly = [
  { key: 'partnerId', mapping: 'partnerId' },
  { key: 'xid', mapping: 'xid' }
];

const channelDealerFields = [
  { key: 'dealership', mapping: 'dealership' },
  { key: 'channelOrgCode', mapping: 'channelOrgCode' }
];

const isChannelSelected = (channels, channel) => {
  if (!channels || !channels.length) return false;
  return channels[0].id === channel;
};

const isMultiChannelSelected = (channels, channel) =>
  channels && channels.length > 0 && channels.includes(channel);

const arrayToJson = (json, { key, mapping }) => ({
  ...json,
  [key]: validationJSON[mapping]
});

const defaultSchema = defaultBaseMapping.reduce(arrayToJson, {});

const fieldsNoChannelAndXid = _.concat(
  defaultBaseMapping.filter(i => i.key !== 'channel'),
  xidField
);

const schemaNoChannelAndXid = fieldsNoChannelAndXid.reduce(arrayToJson, {});

const channelsClause = channels =>
  isChannelSelected(channels, CONSTANT.CHANNELS.BCX_CARE) ||
  isChannelSelected(channels, CONSTANT.CHANNELS.HEALTH) ||
  isChannelSelected(channels, CONSTANT.CHANNELS.CSD) ||
  isChannelSelected(channels, CONSTANT.CHANNELS.NBD) ||
  isChannelSelected(channels, CONSTANT.CHANNELS.ADT);

const checkFieldForSalesRepManager = (field, channels) => {
  const channelId = _.get(channels, '0.id', 0);
  const channelName = _.get(channels, '0.name', '');
  let result = [...defaultBaseMapping, ...commonBetweenRepAndManager]
    .map(v => v.key)
    .includes(field);

  if (!result) {
    result = Object.keys(channelOutletMapping).includes(channelName);
  }
  if (!result && channelId === CONSTANT.CHANNELS.CORP_STORE) {
    result = channelCorpStoreOnly.map(c => c.key).includes(field);
  }
  if (!result && channelId === CONSTANT.CHANNELS.DEALER) {
    result = channelDealerFields.map(c => c.key).includes(field);
  }
  if (!result && channelId === CONSTANT.CHANNELS.TQ_PARTENAIRE) {
    result = channelTqPartenaireOnly.map(c => c.key).includes(field);
  }
  if (!result && channelsClause(channels)) {
    result = xidField.map(c => c.key).includes(field);
  }
  if (result && channelsClause(channels)) {
    result = !['salesRepId', 'outlet'].includes(field);
  }

  return result;
};

const checkFieldForSalesSupport = (field, channels) => {
  let result = [
    ...defaultBaseMapping,
    ...rolebaseMapping[CONSTANT.KEYS.ROLES.SALES_SUPPORT]
  ];

  if (isMultiChannelSelected(channels, CONSTANT.CHANNELS.CORP_STORE)) {
    result = result.concat(channelCorpStoreOnly);
  }
  if (isMultiChannelSelected(channels, CONSTANT.CHANNELS.DEALER)) {
    result = result.concat(channelDealerFields);
  }
  if (channelsClause(channels)) {
    result = result.concat(xidField);
    if (['salesRepId', 'outlet'].includes(field)) {
      return false;
    }
  }

  return result.map(v => v.key).includes(field);
};

export const checkFieldToDisplay = (baseRoleId, field, channelIds) => {
  const roleId = parseInt(baseRoleId, 10);
  let result;
  switch (roleId) {
    case CONSTANT.ROLES.SALES_MANAGER:
    case CONSTANT.ROLES.SALES_REP:
      result = checkFieldForSalesRepManager(field, channelIds);
      break;
    case CONSTANT.ROLES.SALES_SUPPORT:
      result = checkFieldForSalesSupport(field, channelIds);
      break;
    case CONSTANT.ROLES.ADMIN:
    case CONSTANT.ROLES.PROPOSAL_HOUSE:
    case CONSTANT.ROLES.ORDER_FULFILLMENT:
      result = fieldsNoChannelAndXid.map(v => v.key).includes(field);
      break;
    default:
      result = false;
  }
  return result;
};

// Formik schema part
const schemaSalesRepManager = channels => {
  let result = [...defaultBaseMapping, ...commonBetweenRepAndManager];
  if (isChannelSelected(channels, CONSTANT.CHANNELS.CORP_STORE)) {
    result = result.concat(channelCorpStoreOnly);
  }
  if (isChannelSelected(channels, CONSTANT.CHANNELS.DEALER)) {
    result = result.concat(channelDealerFields);
  }
  if (isChannelSelected(channels, CONSTANT.CHANNELS.TQ_PARTENAIRE)) {
    result = result.concat(channelTqPartenaireOnly);
  }
  if (channelsClause(channels)) {
    result = result.concat(xidField);
    // Remove some fields
    result = result.filter(
      ({ key }) => key !== 'salesRepId' && key !== 'outlet'
    );
  }
  result = result.reduce(arrayToJson, {});
  result = Yup.object().shape(result);
  return result;
};

const schemaSalesSupport = channels => {
  let result = [
    ...defaultBaseMapping,
    ...rolebaseMapping[CONSTANT.KEYS.ROLES.SALES_SUPPORT]
  ];
  if (isMultiChannelSelected(channels, CONSTANT.CHANNELS.CORP_STORE)) {
    result = result.concat(channelCorpStoreOnly);
  }
  if (isMultiChannelSelected(channels, CONSTANT.CHANNELS.DEALER)) {
    result = result.concat(channelDealerFields);
  }
  result = result.reduce(arrayToJson, {});
  return Yup.object().shape(result);
};

export const schema = ({ roleId, channel }) => {
  const roleIdParsed = parseInt(roleId, 10);

  switch (roleIdParsed) {
    case CONSTANT.ROLES.SALES_REP:
    case CONSTANT.ROLES.SALES_MANAGER:
      return schemaSalesRepManager(channel);
    case CONSTANT.ROLES.SALES_SUPPORT:
      return schemaSalesSupport(channel);
    case CONSTANT.ROLES.ADMIN:
    case CONSTANT.ROLES.PROPOSAL_HOUSE:
    case CONSTANT.ROLES.ORDER_FULFILLMENT:
      return Yup.object().shape(schemaNoChannelAndXid);
    default:
      return Yup.object().shape(defaultSchema);
  }
};
